import React, { useState, useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import cx from 'classnames';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

import { isToday, isTomorrow, isTodayAndTomorrow } from './utils';

// for react-day-picker translations
// https://react-day-picker-v7.netlify.app/docs/localization
import MomentLocaleUtils from 'react-day-picker/moment';
import { injectIntl } from 'react-intl';

const selectedClassName = 'me-button__grey-ghost__selected';

const formatDate = ({ from, to, intl }) => {
  const t = intl.messages.modules.Filters.date_range;
  if (!from || !to) {
    return false;
  }

  if (isToday({ from, to })) {
    return t.today;
  }

  if (isTomorrow({ from, to })) {
    return t.tomorrow;
  }

  if (isTodayAndTomorrow({ from, to })) {
    return t.today_and_tomorrow;
  }

  const momentFrom = moment(from);
  const momentTo = moment(to);
  if (momentFrom.isSame(momentTo, 'day')) {
    return momentFrom.format(t.date_formatted);
  }

  return `${momentFrom.format(t.date_formatted)} - ${momentTo.format(t.date_formatted)}`;
};

const getDefaultState = (value) => {
  if (!value) {
    return { from: undefined, to: undefined };
  }
  const { from, to } = value;

  return {
    from: moment(from).toDate(),
    to: moment(to).toDate(),
  };
};

const DateRangeFilter = ({ value, className, onChange, dateFormat, intl }) => {
  const node = useRef();

  const defaultState = getDefaultState(value);

  const [opened, setOpened] = useState(false);
  const [{ from, to }, setDate] = useState(defaultState);

  const today = moment();
  const tomorrow = moment().add(1, 'day');

  const handleClickOutside = useCallback((e) => {
    if (node.current.contains(e.target)) {
      return;
    }
    setOpened(false);
  }, []);

  const onDayClick = useCallback(
    (day) => {
      const range = DateUtils.addDayToRange(day, { from, to });
      setDate(range);
    },
    [from, to]
  );

  const changeValue = useCallback(
    ({ from, to }) => {
      const fromFormatted = from.format(dateFormat);
      const toFormatted = to.format(dateFormat);
      setDate({ from: from.toDate(), to: to.toDate() });
      onChange({ from: fromFormatted, to: toFormatted });
      setOpened(false);
    },
    [onChange, dateFormat]
  );

  const handleTodayClick = useCallback(() => {
    changeValue({ from: today, to: today });
  }, [changeValue, today]);

  const handleTomorrowClick = useCallback(() => {
    changeValue({ from: tomorrow, to: tomorrow });
  }, [changeValue, tomorrow]);

  const handleTodayAndTomorrowClick = useCallback(() => {
    changeValue({ from: today, to: tomorrow });
  }, [changeValue, today, tomorrow]);

  const handleFilterClick = useCallback(() => {
    setOpened((opened) => !opened);
  }, []);

  const handleCancelClick = useCallback(() => {
    setOpened(false);
  }, []);

  const handleApplyClick = useCallback(() => {
    changeValue({ from: moment(from), to: moment(to) });
  }, [from, to, changeValue]);

  const modifiers = { start: from, end: to };

  const applyDisabled = !from;

  const isTodaySelected = isToday({ from, to });
  const isTomorrowSelected = isTomorrow({ from, to });
  const isTodayAndTomorrowSelected = isTodayAndTomorrow({ from, to });

  useEffect(() => {
    if (opened) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [opened, handleClickOutside]);

  const placeholder = formatDate({ from, to, intl });
  const t = intl.messages.modules.Filters.date_range;
  return (
    <div className={className} ref={node} data-testid="date_range_filter">
      <div
        className={cx('input', {
          input__opened: opened,
        })}
        onClick={handleFilterClick}
        data-testid="date_range_filter_fake_input"
      >
        {!!placeholder && <span>{placeholder}</span>}
      </div>
      {opened && (
        <div className="tooltip" data-testid="date_range_filter_tooltip">
          <div className="buttons-container">
            <button
              className={cx('me-button me-button__grey-ghost', {
                [selectedClassName]: isTodaySelected,
              })}
              onClick={handleTodayClick}
              data-testid="date_range_filter_today"
            >
              {t.today}
            </button>
            <button
              className={cx('me-button me-button__grey-ghost', {
                [selectedClassName]: isTomorrowSelected,
              })}
              onClick={handleTomorrowClick}
              data-testid="date_range_filter_tomorrow"
            >
              {t.tomorrow}
            </button>
            <button
              className={cx('me-button me-button__grey-ghost', {
                [selectedClassName]: isTodayAndTomorrowSelected,
              })}
              onClick={handleTodayAndTomorrowClick}
              data-testid="date_range_filter_today_and_tomorrow"
            >
              {t.today_and_tomorrow}
            </button>
          </div>
          <div className="calendar">
            <DayPicker
              localeUtils={MomentLocaleUtils}
              locale={intl.locale}
              className="Selectable"
              numberOfMonths={2}
              selectedDays={[from, { from, to }]}
              onDayClick={onDayClick}
              modifiers={modifiers}
              month={today.toDate()}
            />
          </div>
          <div className="buttons-container buttons-container__right">
            <button className="me-button" onClick={handleCancelClick}>
              {t.cancel}
            </button>
            <button
              className="me-button me-button__primary-solid-dark"
              disabled={applyDisabled}
              onClick={handleApplyClick}
              data-testid="date_range_filter_apply"
            >
              {t.apply}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default injectIntl(DateRangeFilter);
